/**
 * Street rig: cool-blue hemisphere ambient (silhouettes against haze), faint
 * cold sky fill, warm sodium streetlamp spotlights forming distinct pools,
 * background window practicals for depth layering, and the single accent
 * spot pooled over the matchbook clue.
 * Owned by LIGHT; see CONTRACTS.md.
 */
import {
  DirectionalLight,
  HemisphereLight,
  Object3D,
  PointLight,
  Scene,
  SpotLight
} from 'three'
import type { Vec3 } from '../core/types'
import { lerp } from './blend'
import {
  CLUE_SPOT_ANGLE,
  CLUE_SPOT_COLOR,
  CLUE_SPOT_DECAY,
  CLUE_SPOT_DISTANCE,
  CLUE_SPOT_INTENSITY_INDOOR,
  CLUE_SPOT_INTENSITY_OUTDOOR,
  CLUE_SPOT_PENUMBRA,
  CLUE_SPOT_POSITION,
  HEMI_GROUND_COLOR,
  HEMI_INTENSITY_INDOOR,
  HEMI_INTENSITY_OUTDOOR,
  HEMI_SKY_COLOR,
  LAMP_ANGLE,
  LAMP_COLOR,
  LAMP_DECAY,
  LAMP_DISTANCE,
  LAMP_HEIGHT,
  LAMP_INTENSITY_INDOOR,
  LAMP_INTENSITY_OUTDOOR,
  LAMP_LATERAL_X,
  LAMP_PENUMBRA,
  LAMP_SHADOW_BIAS,
  LAMP_SHADOW_MAP_SIZE,
  LAMP_SHADOW_NEAR,
  LAMP_Z_POSITIONS,
  PRACTICAL_DECAY,
  PRACTICAL_DEFS,
  PRACTICAL_INDOOR_SCALE,
  SKY_FILL_COLOR,
  SKY_FILL_INTENSITY_INDOOR,
  SKY_FILL_INTENSITY_OUTDOOR,
  SKY_FILL_POSITION
} from './constants'

export interface StreetRig {
  /** World positions of the lamp heads (fed to the rain shader + cones). */
  readonly lampPositions: readonly Vec3[]
  /** Window practicals + clue accent positions (fed to the rain shader). */
  readonly accentPositions: readonly Vec3[]
  /** weight: 1 = outdoor, 0 = indoor. dim: finale multiplier. */
  applyBlend(weight: number, dim: number): void
}

const createLamp = (x: number, z: number): SpotLight => {
  const lamp = new SpotLight(
    LAMP_COLOR,
    LAMP_INTENSITY_OUTDOOR,
    LAMP_DISTANCE,
    LAMP_ANGLE,
    LAMP_PENUMBRA,
    LAMP_DECAY
  )
  lamp.position.set(x, LAMP_HEIGHT, z)
  lamp.target.position.set(x, 0, z)
  lamp.castShadow = true
  lamp.shadow.mapSize.set(LAMP_SHADOW_MAP_SIZE, LAMP_SHADOW_MAP_SIZE)
  lamp.shadow.bias = LAMP_SHADOW_BIAS
  lamp.shadow.camera.near = LAMP_SHADOW_NEAR
  lamp.shadow.camera.far = LAMP_DISTANCE
  return lamp
}

const createClueSpot = (scene: Scene): SpotLight => {
  const spot = new SpotLight(
    CLUE_SPOT_COLOR,
    CLUE_SPOT_INTENSITY_OUTDOOR,
    CLUE_SPOT_DISTANCE,
    CLUE_SPOT_ANGLE,
    CLUE_SPOT_PENUMBRA,
    CLUE_SPOT_DECAY
  )
  spot.position.set(CLUE_SPOT_POSITION.x, CLUE_SPOT_POSITION.y, CLUE_SPOT_POSITION.z)
  spot.target.position.set(CLUE_SPOT_POSITION.x, 0, CLUE_SPOT_POSITION.z)
  spot.castShadow = false
  scene.add(spot)
  scene.add(spot.target)
  return spot
}

export const createStreetRig = (scene: Scene): StreetRig => {
  const hemisphere = new HemisphereLight(
    HEMI_SKY_COLOR,
    HEMI_GROUND_COLOR,
    HEMI_INTENSITY_OUTDOOR
  )
  scene.add(hemisphere)

  const skyFill = new DirectionalLight(SKY_FILL_COLOR, SKY_FILL_INTENSITY_OUTDOOR)
  skyFill.position.set(SKY_FILL_POSITION.x, SKY_FILL_POSITION.y, SKY_FILL_POSITION.z)
  skyFill.castShadow = false
  scene.add(skyFill)
  scene.add(skyFill.target)

  const lamps: SpotLight[] = LAMP_Z_POSITIONS.map((z, index) => {
    const x = index % 2 === 0 ? -LAMP_LATERAL_X : LAMP_LATERAL_X
    return createLamp(x, z)
  })
  const targets: Object3D[] = lamps.map((lamp) => lamp.target)
  lamps.forEach((lamp) => scene.add(lamp))
  targets.forEach((target) => scene.add(target))

  const practicals: PointLight[] = PRACTICAL_DEFS.map((def) => {
    const light = new PointLight(def.color, def.intensity, def.distance, PRACTICAL_DECAY)
    light.position.set(def.position.x, def.position.y, def.position.z)
    light.castShadow = false
    scene.add(light)
    return light
  })

  const clueSpot = createClueSpot(scene)

  const lampPositions: readonly Vec3[] = lamps.map((lamp) => ({
    x: lamp.position.x,
    y: lamp.position.y,
    z: lamp.position.z
  }))

  const accentPositions: readonly Vec3[] = [
    ...PRACTICAL_DEFS.map((def) => def.position),
    CLUE_SPOT_POSITION
  ]

  const applyBlend = (weight: number, dim: number): void => {
    hemisphere.intensity =
      lerp(HEMI_INTENSITY_INDOOR, HEMI_INTENSITY_OUTDOOR, weight) * dim
    skyFill.intensity = lerp(SKY_FILL_INTENSITY_INDOOR, SKY_FILL_INTENSITY_OUTDOOR, weight) * dim
    const lampIntensity = lerp(LAMP_INTENSITY_INDOOR, LAMP_INTENSITY_OUTDOOR, weight) * dim
    lamps.forEach((lamp) => {
      lamp.intensity = lampIntensity
    })
    const practicalScale = lerp(PRACTICAL_INDOOR_SCALE, 1, weight) * dim
    practicals.forEach((light, index) => {
      const def = PRACTICAL_DEFS[index]
      if (def) light.intensity = def.intensity * practicalScale
    })
    clueSpot.intensity =
      lerp(CLUE_SPOT_INTENSITY_INDOOR, CLUE_SPOT_INTENSITY_OUTDOOR, weight) * dim
  }

  return { lampPositions, accentPositions, applyBlend }
}
